/**
 * MobileCollabsManagersList
 *
 * Phone replacement for the five-column managers table (collab, email,
 * matricule, team, profile). Those columns declare ~52rem of minimum width,
 * so on a 360px screen the desktop table can only be read by scrolling
 * sideways; here each manager is one card with labelled lines.
 *
 * Read-only, presentational.
 */
import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Langs from "../../../app/lang/langs";

function matchesFilter(manager, filter) {
    if (!filter) return true;
    const needle = filter.toLowerCase();
    const haystack = [
        manager.prenom,
        manager.nom,
        manager.email,
        manager.matricule,
        manager.team_name,
        manager.profile_name,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return haystack.includes(needle);
}

function Field({ labelKey, value }) {
    if (!value) return null;
    return (
        <Box sx={{ display: "flex", gap: 1, py: 0.35 }}>
            <Typography
                component="span"
                sx={{
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: "rgba(0, 40, 128, 0.45)",
                    flexShrink: 0,
                    minWidth: 78,
                }}
            >
                <Langs str={labelKey} />
            </Typography>
            <Typography
                component="span"
                sx={{ fontSize: "0.85rem", color: "rgba(0, 40, 128, 0.8)", overflowWrap: "anywhere" }}
            >
                {value}
            </Typography>
        </Box>
    );
}

export default function MobileCollabsManagersList({ managers, filter }) {
    const visible = (managers || []).filter((m) => matchesFilter(m, filter));

    if (!visible.length) {
        return (
            <Box sx={{ py: 4, textAlign: "center", color: "rgba(0, 40, 128, 0.5)", fontSize: "0.9rem" }}>
                <Langs str="NO_RESULTS" />
            </Box>
        );
    }

    return (
        <Box>
            {visible.map((manager, idx) => (
                <Card
                    key={manager.id ?? `${manager.email}-${idx}`}
                    elevation={0}
                    sx={{
                        mb: 1.5,
                        borderRadius: "16px",
                        bgcolor: "rgba(255,255,255,0.94)",
                        border: "1px solid rgba(0, 40, 128, 0.08)",
                        boxShadow: "0 1px 2px rgba(0, 40, 128, 0.04), 0 4px 12px rgba(0, 40, 128, 0.05)",
                    }}
                >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                mb: 0.75,
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "1rem",
                                    color: "rgb(0, 40, 128)",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {`${manager.prenom ?? ""} ${manager.nom ?? ""}`.trim()}
                            </Typography>
                            {manager.profile_name && (
                                <Chip
                                    label={manager.profile_name}
                                    size="small"
                                    sx={{
                                        height: 26,
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        borderRadius: "8px",
                                        flexShrink: 0,
                                        bgcolor: "rgba(255, 144, 21, 0.12)",
                                        color: "#c96f00",
                                    }}
                                />
                            )}
                        </Box>

                        <Field labelKey="EMAIL" value={manager.email} />
                        <Field labelKey="MATRICULE" value={manager.matricule} />
                        <Field labelKey="TEAM" value={manager.team_name} />
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
}
